import {
  ActionIcon as MantineActionIcon,
  Button,
  Flex,
  MediaQuery,
  SimpleGrid,
  Title,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { filter } from "lodash";
import isEqual from "lodash/isEqual";
import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useClikCities } from "../../api/hooks/clikData/useClikCities.ts";
import { useClikCountries } from "../../api/hooks/clikData/useClikCountries.ts";
import { useCreateClikData } from "../../api/hooks/clikData/useCreateClikData.ts";
import { useCompanyOptions } from "../../api/hooks/customer/useCompanyOptions.ts";
import MultiFieldCard from "../../components/Cards/MultiFieldCard/MultiFieldCard.tsx";
import ClikDataInfoForm from "../../components/ClikDataInfoForm/ClikDataInfoForm.tsx";
import { GroupFormModal } from "../../components/ClikDataInfoForm/GroupFormModal/GroupFormModal.tsx";
import PengurusFormModal from "../../components/ClikDataInfoForm/PengurusFormModal/PengurusFormModal.tsx";
import { ClikDataFormOut } from "../../components/ClikDataInfoForm/types.ts";
import SvgChevronLeft from "../../components/Icons/ChevronLeft.tsx";
import Plus from "../../components/Icons/Plus.tsx";
import Layout from "../../components/Layout/Layout.tsx";
import Preloader from "../../components/Preloader/Preloader.tsx";
import SectionTitle from "../../components/Section/SectionTitle/SectionTitle.tsx";
import { AppRoute } from "../../constants.ts";
import { useMutationNotificationWrapper } from "../../hooks/useMutationNotificationWrapper.tsx";
import { RawGroup } from "../../types/clikData/group.ts";
import { RawPengurus } from "../../types/clikData/pengurus.ts";
import { useStyles } from "./ClikDataCreate.styles.ts";
import { useGroupsInfo } from "./hooks/useGroupsInfo.tsx";
import { usePengurusesInfo } from "./hooks/usePengurusesInfo.tsx";
import { formatToCreateClikDataInput } from "./utils.ts";

const ClikDataCreatePage = () => {
  const { classes } = useStyles();

  const navigate = useNavigate();

  const [value, setValue] = useState<ClikDataFormOut | null>(null);

  const [valid, setValid] = useState(false);

  const [penguruses, setPenguruses] = useState<RawPengurus[]>([]);
  const [groups, setGroups] = useState<RawGroup[]>([]);

  const [selectedPengurus, setSelectedPengurus] = useState<{
    index: number;
    data: RawPengurus;
  } | null>(null);
  const [selectedGroup, setSelectedGroup] = useState<{
    index: number;
    data: RawGroup;
  } | null>(null);

  const [{ data: clikCitiesData, fetching: clikCitiesFetching }] =
    useClikCities();

  const [{ data: clikCountriesData, fetching: clikCountriesFetching }] =
    useClikCountries();

  const [{ data: companiesData, fetching: companiesFetching }] =
    useCompanyOptions();
  const customers = companiesData?.customers.data;

  const [, createClikData] = useMutationNotificationWrapper(
    useCreateClikData(),
    {
      success: {
        message: "Clik Data has been created successfully.",
      },
    }
  );

  const [
    pengurusModalOpened,
    { open: pengurusModalOpen, close: pengurusModalClose },
  ] = useDisclosure(false);
  const [groupModalOpened, { open: groupModalOpen, close: groupModalClose }] =
    useDisclosure(false);

  const handleValueChange = useCallback((newValue: ClikDataFormOut) => {
    setValue((prevValue) => {
      if (isEqual(prevValue, newValue)) {
        return prevValue;
      }
      return newValue;
    });
  }, []);

  const handleValidChange = useCallback((isValid: boolean) => {
    setValid(isValid);
  }, []);

  const handleBack = () => navigate(AppRoute.ClikData);

  const handleSubmit = () => {
    if (!value) {
      return;
    }
    const input = formatToCreateClikDataInput(value, penguruses, groups);

    if (!input) {
      return;
    }
    createClikData({ input }).then(() => {
      handleBack();
    });
  };

  const handleCancel = () => {
    setValue(null);
    handleBack();
  };

  const handlePengurusModalClose = () => {
    setSelectedPengurus(null);
    pengurusModalClose();
  };

  const handleGroupModalClose = () => {
    setSelectedGroup(null);
    groupModalClose();
  };

  const handleStartEditPengurus = (value: RawPengurus, index: number) => {
    setSelectedPengurus({ index, data: value });
    pengurusModalOpen();
  };

  const handleStartEditGroup = (value: RawGroup, index: number) => {
    setSelectedGroup({ index, data: value });
    groupModalOpen();
  };

  const handleSavePengurus = (value: RawPengurus) => {
    if (selectedPengurus) {
      handleEditPengurus(value);
    } else {
      handleAddPengurus(value);
    }
  };

  const handleSaveGroup = (value: RawGroup) => {
    if (selectedGroup) {
      handleEditGroup(value);
    } else {
      handleAddGroup(value);
    }
  };

  const handleAddPengurus = (value: RawPengurus) =>
    setPenguruses((prevValue) => [...prevValue, value]);

  const handleEditPengurus = (value: RawPengurus) => {
    if (!selectedPengurus) {
      return;
    }
    setPenguruses((prevArray) => {
      return prevArray.map((v, i) => {
        if (i === selectedPengurus.index) {
          return value;
        }
        return v;
      });
    });
  };

  const handleAddGroup = (value: RawGroup) =>
    setGroups((prevValue) => [...prevValue, value]);

  const handleEditGroup = (value: RawGroup) => {
    if (!selectedGroup) {
      return;
    }
    setGroups((prevArray) => {
      return prevArray.map((v, i) => {
        if (i === selectedGroup.index) {
          return value;
        }
        return v;
      });
    });
  };

  const handleDeletePengurus = (index: number) => {
    setPenguruses((prevValue) => filter(prevValue, (_, i) => i !== index));
  };

  const handleDeleteGroup = (index: number) => {
    setGroups((prevValue) => filter(prevValue, (_, i) => i !== index));
  };

  const pengurusesInfoCards = usePengurusesInfo({
    penguruses,
    onEdit: handleStartEditPengurus,
    onDelete: handleDeletePengurus,
  });

  const groupsInfoCards = useGroupsInfo({
    groups,
    onEdit: handleStartEditGroup,
    onDelete: handleDeleteGroup,
  });

  const isFetching = useMemo(() => {
    return clikCitiesFetching || clikCountriesFetching || companiesFetching;
  }, [clikCitiesFetching, companiesFetching, clikCountriesFetching]);

  return (
    <>
      <Layout>
        <Preloader loading={isFetching} />
        <Flex direction="column" className={classes.wrapper}>
          <Flex
            justify="space-between"
            align="center"
            pb={{ base: 16, sm: 24 }}
          >
            <Flex gap={20} align="center">
              <MantineActionIcon
                size="m"
                variant="outlineGolden"
                onClick={handleBack}
              >
                <SvgChevronLeft />
              </MantineActionIcon>
              <Title className={classes.titleWrapper}>Add CLIK Data</Title>
            </Flex>
            <MediaQuery smallerThan="sm" styles={{ display: "none" }}>
              <Flex gap={20}>
                <Button
                  variant="outlineGolden"
                  type="reset"
                  uppercase
                  onClick={handleCancel}
                >
                  cancel
                </Button>
                <Button
                  variant="filledGolden"
                  type="submit"
                  disabled={!valid}
                  uppercase
                  onClick={handleSubmit}
                >
                  submit
                </Button>
              </Flex>
            </MediaQuery>
          </Flex>
          <Flex direction="column" gap={{ base: 16, sm: 20 }}>
            <ClikDataInfoForm
              value={null}
              cities={clikCitiesData?.clikCities}
              countries={clikCountriesData?.clikCountries}
              companies={customers}
              onValueChange={handleValueChange}
              onValidChange={handleValidChange}
            />
            <SimpleGrid cols={2} verticalSpacing={16} spacing={20}>
              <Flex direction="column" gap={{ base: 16, sm: 20 }}>
                <SectionTitle flex={0}>Pengurus List</SectionTitle>
                <Button
                  leftIcon={<Plus />}
                  variant="filledGolden"
                  uppercase
                  onClick={pengurusModalOpen}
                >
                  add
                </Button>
                <SimpleGrid cols={2} verticalSpacing={16} spacing={20}>
                  {pengurusesInfoCards?.map((item, index) => (
                    <MultiFieldCard key={index} variant="blue" {...item} />
                  ))}
                </SimpleGrid>
              </Flex>
              <Flex direction="column" gap={{ base: 16, sm: 20 }}>
                <SectionTitle flex={0}>Group Info</SectionTitle>
                <Button
                  leftIcon={<Plus />}
                  variant="filledGolden"
                  uppercase
                  onClick={groupModalOpen}
                >
                  add
                </Button>
                <SimpleGrid cols={2} verticalSpacing={16} spacing={20}>
                  {groupsInfoCards?.map((item, index) => (
                    <MultiFieldCard key={index} variant="blue" {...item} />
                  ))}
                </SimpleGrid>
              </Flex>
            </SimpleGrid>
          </Flex>
        </Flex>
        <MediaQuery largerThan="sm" styles={{ display: "none" }}>
          <Flex gap={20} className={classes.buttonsWrapper}>
            <Button
              variant="outlineGolden"
              type="reset"
              uppercase
              w="100%"
              onClick={handleCancel}
            >
              cancel
            </Button>
            <Button
              variant="filledGolden"
              type="submit"
              uppercase
              w="100%"
              disabled={!valid}
              onClick={handleSubmit}
            >
              submit
            </Button>
          </Flex>
        </MediaQuery>
      </Layout>
      {pengurusModalOpened && (
        <PengurusFormModal
          value={selectedPengurus?.data ?? null}
          cities={clikCitiesData?.clikCities}
          countries={clikCountriesData?.clikCountries}
          opened={true}
          onClose={handlePengurusModalClose}
          onSubmit={handleSavePengurus}
        />
      )}
      {groupModalOpened && (
        <GroupFormModal
          value={selectedGroup?.data ?? null}
          cities={clikCitiesData?.clikCities}
          countries={clikCountriesData?.clikCountries}
          opened={true}
          onClose={handleGroupModalClose}
          onSubmit={handleSaveGroup}
        />
      )}
    </>
  );
};

export default ClikDataCreatePage;
